import styled from "@emotion/styled";
import {
  AppBar,
  Box,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  TextField as MuiTextField,
  Radio,
  RadioGroup,
  Toolbar,
  Typography
} from "@mui/material";
import { JSX, useCallback, useEffect, useMemo, useState } from "react";
import { Control, Controller, useForm } from "react-hook-form";
import { Utils } from "../../common/Utils";
import { Button } from "../../components/Button";
import { DatePicker } from "../../components/DatePicker";
import { CHECKLIST_QUESTION_TYPE } from "../../constants";
import { useGetCheckListById } from "../../hooks/useCheckListMutation";
import { ICheckLists } from "../../interfaces/CheckLists";
import {
  Answer,
  AnswerPath,
  ChecklistAnswers,
  FormAnswer,
  FormValues
} from "../../interfaces/Participants";
import { useLocale } from "../../providers/LocaleProvider";
import Logo from "../../resources/images/logo.svg";
import {
  ChecklistOptionType,
  ChecklistQuestionType,
  ChecklistSectionType
} from "../../utilities/checkListsSchema";

const CheckListsPreviewDialog = ({
  isOpen,
  onClose,
  checklistId,
  checklistData,
  onSave,
  isEditMode = false,
  disabled = false
}: {
  isOpen: boolean;
  onClose: () => void;
  checklistId?: string;
  checklistData?: ICheckLists;
  onSave?: (payload: ChecklistAnswers) => void;
  isEditMode?: boolean;
  disabled?: boolean;
}) => {
  const { translate } = useLocale();
  const { checklist: checklistAPI, isLoading } = useGetCheckListById(checklistId || "", isOpen);
  const checklists = checklistId ? checklistAPI : checklistData;

  const [invalidDateQuestions, setInvalidDateQuestions] = useState<Record<string, boolean>>({});

  const updateDateValidity = useCallback((questionId: string, isValid?: boolean) => {
    setInvalidDateQuestions((prev) => {
      const nextValue = !isValid;
      if (prev[questionId] === nextValue) {
        return prev;
      }
      return {
        ...prev,
        [questionId]: nextValue
      };
    });
  }, []);

  const QUESTION_TYPE_PREVIEWS = useMemo(
    () =>
      new Map<
        string,
        (question: ChecklistQuestionType, control: Control<FormValues>) => JSX.Element
      >([
        [
          CHECKLIST_QUESTION_TYPE.SINGLE_CHOICE,
          (question: ChecklistQuestionType, control: Control<FormValues>) => {
            return (
              <Box
                key={CHECKLIST_QUESTION_TYPE.SINGLE_CHOICE}
                sx={{ display: "flex", flexDirection: "column", mt: 1 }}
              >
                <Controller
                  name={`answers.${question.id}.selectedOptionIds` as AnswerPath}
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field}>
                      {question.options?.map((option: ChecklistOptionType) => (
                        <FormControlLabel
                          key={option.id}
                          value={option.id}
                          control={
                            <Radio
                              size='small'
                              inputProps={{
                                disabled: disabled
                              }}
                            />
                          }
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                width: "100%",
                                position: "relative"
                              }}
                            >
                              <Typography
                                className='klayo-paper_title !important'
                                variant='body1'
                                sx={{ width: "100%" }}
                              >
                                {option.optionText}
                              </Typography>
                            </Box>
                          }
                          sx={{
                            width: "100%",
                            "&:hover": {
                              backgroundColor: "var(--grey-gr-050)"
                            }
                          }}
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
              </Box>
            );
          }
        ],
        [
          CHECKLIST_QUESTION_TYPE.MULTIPLE_CHOICE,
          (question: ChecklistQuestionType, control: Control<FormValues>) => (
            <Box
              key={CHECKLIST_QUESTION_TYPE.MULTIPLE_CHOICE}
              sx={{ display: "flex", flexDirection: "column", mt: 1 }}
            >
              <Controller
                name={`answers.${question.id}.selectedOptionIds` as AnswerPath}
                control={control}
                render={({ field }) => (
                  <>
                    {question.options?.map((option: ChecklistOptionType) => {
                      const selected: string[] = Array.isArray(field.value) ? field.value : [];
                      const isChecked = selected.includes(option.id);
                      const toggle = (_: any, checked: boolean) => {
                        const next = checked
                          ? [...selected, option.id]
                          : selected.filter((id) => id !== option.id);
                        field.onChange(next);
                      };
                      return (
                        <FormControlLabel
                          key={option.id}
                          control={
                            <Checkbox
                              size='small'
                              checked={isChecked}
                              onChange={toggle}
                              inputProps={{
                                disabled: disabled
                              }}
                            />
                          }
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                width: "100%",
                                position: "relative"
                              }}
                            >
                              <Typography className='klayo-paper_title !important' variant='body1'>
                                {option.optionText}
                              </Typography>
                            </Box>
                          }
                          sx={{
                            width: "100%",
                            "&:hover": {
                              backgroundColor: "var(--grey-gr-050)"
                            }
                          }}
                        />
                      );
                    })}
                  </>
                )}
              />
            </Box>
          )
        ],
        [
          CHECKLIST_QUESTION_TYPE.DATE,
          (question: ChecklistQuestionType, control: Control<FormValues>) => (
            <Box key={CHECKLIST_QUESTION_TYPE.DATE} sx={{ marginTop: 1, width: "100%" }}>
              <Controller
                name={`answers.${question.id}.dateAnswer` as AnswerPath}
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value as Date}
                    onChange={(value, isValid) => {
                      field.onChange(value);
                      updateDateValidity(question.id, isValid);
                    }}
                    sx={{ width: "100%" }}
                    isCustom={false}
                    readOnly={disabled}
                  />
                )}
              />
            </Box>
          )
        ],
        [
          CHECKLIST_QUESTION_TYPE.SHORT_ANSWER,
          (question: ChecklistQuestionType, control: Control<FormValues>) => (
            <Box key={CHECKLIST_QUESTION_TYPE.SHORT_ANSWER}>
              <Controller
                name={`answers.${question.id}.textAnswer` as AnswerPath}
                control={control}
                render={({ field }) => (
                  <MuiTextField
                    className='klayo-textfield'
                    placeholder='Short text response'
                    fullWidth
                    variant='outlined'
                    size='small'
                    {...field}
                    InputProps={{
                      readOnly: disabled
                    }}
                    inputProps={{
                      maxLength: 500
                    }}
                    helperText={`${field.value && typeof field.value === "string" ? field.value.length : 0}/500`}
                    FormHelperTextProps={{
                      sx: { textAlign: "right", marginLeft: 0 }
                    }}
                  />
                )}
              />
            </Box>
          )
        ],
        [
          CHECKLIST_QUESTION_TYPE.LONG_ANSWER,
          (question: ChecklistQuestionType, control: Control<FormValues>) => (
            <Box key={CHECKLIST_QUESTION_TYPE.LONG_ANSWER}>
              <Controller
                name={`answers.${question.id}.textAnswer` as AnswerPath}
                control={control}
                render={({ field }) => (
                  <MuiTextField
                    className='klayo-textfield'
                    placeholder='Long text response'
                    fullWidth
                    variant='outlined'
                    size='small'
                    multiline
                    rows={3}
                    {...field}
                    InputProps={{
                      readOnly: disabled
                    }}
                    helperText={`${field.value && typeof field.value === "string" ? field.value.length : 0}/5000`}
                    FormHelperTextProps={{
                      sx: { textAlign: "right", marginLeft: 0 }
                    }}
                    inputProps={{
                      maxLength: 5000
                    }}
                  />
                )}
              />
            </Box>
          )
        ]
      ]),
    [disabled, updateDateValidity]
  );

  const initialFormValues = useMemo(() => {
    const formAnswers: Record<string, FormAnswer> = {};

    checklistData?.answers?.forEach((answer: Answer) => {
      formAnswers[answer.questionId] = {
        selectedOptionIds: answer.selectedOptionIds || [],
        dateAnswer: Utils.parseApiDate(answer.dateAnswer) || null,
        textAnswer: answer.textAnswer || ""
      };
    });

    return { answers: formAnswers };
  }, [checklistData]);

  const form = useForm<FormValues>({
    defaultValues: { answers: {} }
  });

  const hasInvalidDateAnswers = useMemo(
    () => Object.values(invalidDateQuestions).some(Boolean),
    [invalidDateQuestions]
  );

  useEffect(() => {
    if (isOpen) {
      const isHasAnswersUpload =
        checklistData?.answers &&
        Array.isArray(checklistData.answers) &&
        checklistData.answers.length > 0;

      if (isHasAnswersUpload) {
        form.reset(initialFormValues);
      } else {
        form.reset({ answers: {} });
      }
      setInvalidDateQuestions({});
    }
  }, [isOpen, checklistData, initialFormValues, form]);

  const currentQuestionIdSet = useMemo(() => {
    const ids: string[] = (checklists?.sections || [])
      .flatMap((s: ChecklistSectionType) => s.questions || [])
      .map((q: ChecklistQuestionType) => q.id);
    return new Set(ids);
  }, [checklists]);

  const onSubmit = (values: FormValues) => {
    const rawAnswersObj = values?.answers || {};
    const answers = Object.entries(rawAnswersObj)
      .filter(([questionId]) => currentQuestionIdSet.has(String(questionId)))
      .map(([questionId, ans]: [string, FormAnswer]) => {
        const selected = ans?.selectedOptionIds;
        const selectedOptionIds = Array.isArray(selected)
          ? selected
          : selected
            ? [String(selected)]
            : [];
        return {
          questionId: String(questionId),
          selectedOptionIds,
          dateAnswer: ans?.dateAnswer || "",
          textAnswer: (ans?.textAnswer || "").trim()
        };
      });

    const checklistAnswers = {
      checklistId: checklistId || checklists?.id || "",
      answers,
      ...(checklists?.assignmentId && { assignmentId: checklists?.assignmentId })
    };

    if (onSave) {
      onSave(checklistAnswers as ChecklistAnswers);
    }
  };

  return (
    <Dialog open={isOpen} fullScreen={true} onClose={onClose}>
      <DialogContent
        sx={{
          pt: 11
        }}
      >
        <Container sx={{ height: "100%" }}>
          {isLoading ? (
            <Box display={"flex"} height={"100%"} justifyContent={"center"} alignItems={"center"}>
              <CircularProgress />
            </Box>
          ) : (
            <Box height='100%' display={"flex"} flexDirection={"column"} gap={3}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <Grid container spacing={6}>
                  <Grid item xs={18}>
                    <Box>
                      <Typography
                        className='klayo-paper_title !important'
                        sx={{ fontSize: "24px", fontWeight: 600 }}
                      >
                        {checklists?.name}
                      </Typography>
                      {checklists?.sections.map((section) => (
                        <Box key={section.id} sx={{ marginBottom: "64px" }}>
                          <Typography
                            className='klayo-paper_title !important'
                            sx={{
                              marginTop: "40px",
                              fontSize: "20px",
                              fontWeight: 600
                            }}
                          >
                            {section.title}
                          </Typography>
                          <Box>
                            {section.questions.map((question: ChecklistQuestionType) => {
                              const questionType =
                                question?.questionType?.code || question?.questionTypeCode;

                              const renderFunc = questionType
                                ? QUESTION_TYPE_PREVIEWS.get(questionType)
                                : undefined;

                              return (
                                <Box key={question.id} sx={{ marginTop: "24px" }}>
                                  <Typography className='klayo-paper_title !important'>
                                    {question.questionText}
                                  </Typography>
                                  <Box sx={{ width: "100%", marginTop: "16px" }}>
                                    {renderFunc ? renderFunc(question, form.control) : null}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Box>
          )}
        </Container>
      </DialogContent>
      {/* Top App Bar */}
      <TopAppBar>
        <Toolbar>
          <Box
            className='klaro-appbar_logocontainer'
            sx={{ height: { xs: "24px!important", sm: "32px!important" } }}
          >
            <Logo className='klaro-appbar_logo' height='100%' />
          </Box>
          <Box
            sx={{
              gap: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              width: "100%"
            }}
          >
            <Button
              label={translate("common.close")}
              variant='outlined'
              size='md'
              onClick={() => {
                onClose();
              }}
            />
            {isEditMode && (
              <Button
                label={translate("common.save")}
                variant='filled'
                size='md'
                disabled={hasInvalidDateAnswers}
                onClick={form.handleSubmit(onSubmit)}
              />
            )}
          </Box>
        </Toolbar>
      </TopAppBar>
    </Dialog>
  );
};
const TopAppBar = styled(AppBar)`
  top: 0;
  bottom: unset;
  background-color: #fff;
  box-shadow: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
`;
export default CheckListsPreviewDialog;
